import type { ITenantServiceConsumptionItem } from '@experiences/interfaces';
import {
    Box,
    Divider,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    FontVariantToken,
    Spacing,
} from '@uipath/apollo-core';
import { Chart } from '@uipath/apollo-lab/react';
import { ApTypography } from '@uipath/portal-shell-react';
import React, {
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import {
    calcPercent,
    formatConsumptionValue,
} from '../utils';

const MAX_INITIAL_SERVICES = 5;

const useStyles = makeStyles(theme =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: Spacing.SpacingS,
            height: '100%',
            minHeight: 0,
        },
        servicesList: {
            display: 'flex',
            flexDirection: 'column',
            gap: Spacing.SpacingS,
        },
        serviceRow: {
            display: 'flex',
            flexDirection: 'column',
            gap: Spacing.SpacingMicro,
        },
        serviceHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        serviceName: { color: theme.palette.semantic.colorForeground },
        serviceValue: { color: theme.palette.semantic.colorForegroundDeEmp },
        progressTrack: {
            height: 32,
            borderRadius: Border.BorderRadiusM,
            backgroundColor: theme.palette.semantic.colorBackgroundSecondary,
            overflow: 'hidden',
        },
        progressBar: {
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: Spacing.SpacingBase,
            borderRadius: Border.BorderRadiusM,
            backgroundColor: theme.palette.semantic.colorPrimary,
            minWidth: 40,
            transition: 'width 0.2s ease',
        },
        progressBarLabel: { color: theme.palette.semantic.colorForegroundInverse },
        showMoreButton: {
            display: 'flex',
            justifyContent: 'center',
            paddingTop: Spacing.SpacingXs,
        },
        showMoreText: {
            cursor: 'pointer',
            color: theme.palette.semantic.colorPrimary,
            fontWeight: 600,
            '&:hover': { textDecoration: 'underline' },
        },
        divider: {
            marginTop: Spacing.SpacingXs,
            marginBottom: Spacing.SpacingXs,
        },
        totalRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        totalLabel: { color: theme.palette.semantic.colorForeground },
        totalValue: {
            color: theme.palette.semantic.colorPrimary,
            fontWeight: 700,
        },
        footer: { marginTop: 'auto' },
        emptyChartContainer: {
            width: '100%',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
        },
    })
);

interface ConsumptionByServicesProps {
    services: ITenantServiceConsumptionItem[];
    totalConsumption: number;
    unitType: string;
}

export const getConsumptionServiceDisplayName = (
    service: string,
    translate: (descriptor: { id: string }) => string
): string => {
    const raw = service?.trim() || '';
    if (!raw) {
        return raw;
    }

    const normalized = raw
        .toLowerCase()
        .replaceAll(/[^a-z0-9]/g, '');

    const translationCandidates = [
        `CLIENT_CONSUMABLE_GRAPH_${raw}`,
        `CLIENT_CONSUMABLE_GRAPH_${raw.toLowerCase()}`,
        `CLIENT_CONSUMABLE_GRAPH_${normalized}`,
    ];

    for (const candidate of translationCandidates) {
        const translated = translate({ id: candidate });
        if (translated !== candidate) {
            return translated;
        }
    }

    return raw;
};

export const ConsumptionByServices: React.FC<ConsumptionByServicesProps> = ({
    services,
    totalConsumption,
    unitType,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const [ showAll, setShowAll ] = useState(false);

    const sortedServices = useMemo(
        () => [ ...services ].sort((a, b) => b.consumedAmount - a.consumedAmount),
        [ services ]
    );
    const displayServices = showAll
        ? sortedServices
        : sortedServices.slice(0, MAX_INITIAL_SERVICES);
    const hasMore = sortedServices.length > MAX_INITIAL_SERVICES;
    const moreCount = sortedServices.length - MAX_INITIAL_SERVICES;
    const hasData = sortedServices.length > 0 && totalConsumption > 0;

    const getServiceDisplayName = useMemo(
        () => (service: string): string => getConsumptionServiceDisplayName(service, translate),
        [ translate ]
    );

    if (!hasData) {
        return (
            <Box className={classes.emptyChartContainer}>
                <Chart
                    data={undefined}
                    messageMapper={() => translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EMPTY_BY_SERVICE' })}
                />
            </Box>
        );
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.servicesList}>
                {displayServices.map((item) => {
                    const percentage = calcPercent(item.consumedAmount, totalConsumption);

                    return (
                        <Box
                            key={item.service}
                            className={classes.serviceRow}>
                            <Box className={classes.serviceHeader}>
                                <ApTypography
                                    variant={FontVariantToken.fontSizeMBold}
                                    className={classes.serviceName}
                                >
                                    {getServiceDisplayName(item.service)}
                                </ApTypography>
                                <ApTypography
                                    variant={FontVariantToken.fontSizeS}
                                    className={classes.serviceValue}
                                >
                                    {translate(
                                        { id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_SERVICE_UNITS_PERCENT' },
                                        {
                                            units: formatConsumptionValue(item.consumedAmount),
                                            unitType,
                                            percent: percentage,
                                        }
                                    )}
                                </ApTypography>
                            </Box>
                            <Box className={classes.progressTrack}>
                                <Box
                                    className={classes.progressBar}
                                    style={{ width: `${Math.max(percentage, 2)}%` }}
                                >
                                    <ApTypography
                                        variant={FontVariantToken.fontSizeS}
                                        className={classes.progressBarLabel}
                                    >
                                        {`${percentage.toFixed(2)}%`}
                                    </ApTypography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}

                {hasMore && (
                    <Box className={classes.showMoreButton}>
                        <ApTypography
                            variant={FontVariantToken.fontSizeMBold}
                            className={classes.showMoreText}
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowAll(!showAll)}
                            onKeyDown={(e) => e.key === 'Enter' && setShowAll(!showAll)}
                        >
                            {showAll
                                ? translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_SHOW_LESS' })
                                : translate(
                                    { id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_SHOW_MORE_SERVICES' },
                                    { count: moreCount }
                                )}
                        </ApTypography>
                    </Box>
                )}
            </Box>

            <Box className={classes.footer}>
                <Divider className={classes.divider} />

                <Box className={classes.totalRow}>
                    <ApTypography
                        variant={FontVariantToken.fontSizeMBold}
                        className={classes.totalLabel}
                    >
                        {translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_TOTAL_CONSUMPTION' })}
                    </ApTypography>
                    <ApTypography
                        variant={FontVariantToken.fontSizeMBold}
                        className={classes.totalValue}
                    >
                        {translate(
                            { id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_TOTAL_UNITS' },
                            {
                                units: formatConsumptionValue(totalConsumption),
                                unitType,
                            }
                        )}
                    </ApTypography>
                </Box>
            </Box>
        </Box>
    );
};
